import React from 'react';
import { cn } from './cn';

// Pill buttons with the "button-in-button" trailing icon: the icon lives in its own circular
// wrapper flush with the right padding, and translates/scales diagonally on hover for kinetic
// tension. The whole button presses in (active:scale) — real-world mass, custom fluid easing.
const VARIANTS = {
    primary:   'bg-espresso text-cream hover:bg-espresso-deep',
    secondary: 'bg-brass text-cream hover:bg-brass-deep',
    ghost:     'bg-transparent text-espresso ring-1 ring-espresso/15 hover:bg-espresso/[0.05]',
    soft:      'bg-espresso/[0.06] text-espresso hover:bg-espresso/10',
    danger:    'bg-danger text-cream hover:brightness-95',
};
const ICON_WRAP = {
    primary:   'bg-cream/20',
    secondary: 'bg-cream/20',
    ghost:     'bg-espresso/10',
    soft:      'bg-espresso/10',
    danger:    'bg-cream/20',
};
const SIZES = {
    sm: 'px-4 py-2 text-sm',
    md: 'px-6 py-3 text-[15px]',
    lg: 'px-8 py-4 text-base',
};

export function Button({
    children, variant = 'primary', size = 'md', icon: Icon, iconWeight = 'light',
    type = 'button', className, loading = false, disabled, ...rest
}) {
    return (
        <button
            type={type}
            disabled={disabled || loading}
            className={cn(
                'group inline-flex select-none items-center justify-center gap-3 rounded-full font-medium',
                'transition-all duration-500 ease-fluid active:scale-[0.98]',
                'disabled:pointer-events-none disabled:opacity-50',
                VARIANTS[variant], SIZES[size], className,
            )}
            {...rest}
        >
            <span>{children}</span>
            {Icon && (
                <span className={cn(
                    'flex h-8 w-8 items-center justify-center rounded-full transition-transform duration-500 ease-fluid',
                    'group-hover:translate-x-1 group-hover:-translate-y-px group-hover:scale-105',
                    loading && 'animate-spin',
                    ICON_WRAP[variant],
                )}>
                    <Icon size={16} weight={iconWeight} />
                </span>
            )}
        </button>
    );
}

// Circular icon-only button.
export function IconButton({ icon: Icon, label, size = 18, variant = 'soft', className, ...rest }) {
    const v = {
        soft:  'bg-espresso/[0.06] text-espresso hover:bg-espresso/10',
        ghost: 'text-espresso hover:bg-espresso/[0.06]',
    };
    return (
        <button
            aria-label={label}
            className={cn('inline-flex h-10 w-10 items-center justify-center rounded-full transition-all duration-300 ease-fluid active:scale-95', v[variant], className)}
            {...rest}
        >
            <Icon size={size} weight="light" />
        </button>
    );
}
